import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Send, CalendarClock, X, MapPin, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal, Magnetic } from "../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = "https://calendly.com/nexis-info/30min";

const INTERESTS = [
  "Enterprise Transformation",
  "AI Enablement",
  "Data Center Modernization",
  "Construction Program Mgmt",
  "Master Scheduling",
  "Strategic Advisory",
];

const OFFICES = [
  { city: "Virginia", role: "Global HQ" },
  { city: "New York", role: "Office" },
  { city: "London", role: "EMEA" },
  { city: "Singapore", role: "APAC" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [interest, setInterest] = useState(INTERESTS[0]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [calOpen, setCalOpen] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Calendly's inline iframe glitches/stalls on phones — open its mobile-optimized
  // page directly on touch/small devices; keep the embedded popup on desktop.
  const openScheduler = () => {
    const mobile =
      typeof window !== "undefined" &&
      (window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 820);
    if (mobile) {
      window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
    } else {
      setCalOpen(true);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, interest, kind: "contact" });
      setDone(true);
      toast.success("Transmission received. We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative border-t border-white/5 bg-nexis-bg/90 py-28 backdrop-blur-sm md:py-40"
      data-testid="contact-section"
    >
      <Toaster theme="dark" position="bottom-right" richColors />
      <div className="orb orb-purple right-[10%] top-[10%] h-[380px] w-[380px]" />
      <div className="orb orb-blue left-[8%] bottom-[10%] h-[360px] w-[360px]" />

      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr]">
          {/* Left: console intro */}
          <div>
            <Reveal>
              <span className="font-mono text-xs tracking-[0.25em] text-nexis-blue">— COMMUNICATION CONSOLE</span>
              <h2 className="mt-5 font-display text-4xl font-light leading-tight tracking-tight lg:text-6xl">
                Let's build what<br /><span className="text-gradient">comes next</span>.
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-white/60">
                Open a direct line to the Nexis leadership team. Every message reaches a senior operator — no gatekeepers.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <Magnetic strength={0.4} className="mt-8 inline-block">
                <button
                  onClick={openScheduler}
                  className="group flex items-center gap-2 rounded-full glass glass-hover px-6 py-3.5 font-medium"
                  data-testid="open-calendar"
                >
                  <CalendarClock className="h-4 w-4 text-nexis-blue" />
                  Schedule a strategy session
                </button>
              </Magnetic>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-12 space-y-4">
                <a href="mailto:hello@nexis.team" className="flex items-center gap-3 text-sm text-white/60 transition-colors hover:text-white" data-testid="contact-email">
                  <Mail className="h-4 w-4 text-nexis-blue" /> hello@nexis.team
                </a>
                <div className="flex flex-wrap gap-3">
                  {OFFICES.map((o) => (
                    <div key={o.city} className="flex items-center gap-2 rounded-full glass px-4 py-2 text-xs">
                      <MapPin className="h-3 w-3 text-nexis-purple" />
                      <span className="text-white/80">{o.city}</span>
                      <span className="text-white/35">{o.role}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <Reveal delay={0.1}>
            <div className="glass rounded-[2rem] p-8 md:p-10" data-testid="contact-console">
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="done"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center py-16 text-center"
                  >
                    <CheckCircle2 className="h-14 w-14 text-nexis-blue" />
                    <h3 className="mt-6 font-display text-2xl">Message received.</h3>
                    <p className="mt-3 max-w-xs text-sm text-white/55">
                      A member of the Nexis team will respond within one business day.
                    </p>
                    <button
                      onClick={() => setDone(false)}
                      className="mt-8 rounded-full glass glass-hover px-6 py-3 text-sm"
                      data-testid="send-another"
                    >
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onSubmit={submit}
                    className="space-y-5"
                    data-testid="contact-form"
                  >
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Full name" value={form.name} onChange={set("name")} placeholder="Jane Doe" testid="input-name" />
                      <Field label="Work email" type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" testid="input-email" />
                    </div>
                    <Field label="Company" value={form.company} onChange={set("company")} placeholder="Acme Corp" testid="input-company" />

                    <div>
                      <label className="mb-3 block font-mono text-xs tracking-wider text-white/45">AREA OF INTEREST</label>
                      <div className="flex flex-wrap gap-2">
                        {INTERESTS.map((it) => (
                          <button
                            type="button"
                            key={it}
                            onClick={() => setInterest(it)}
                            className={`rounded-full border px-3.5 py-1.5 text-xs transition-all ${
                              interest === it
                                ? "border-nexis-blue bg-nexis-blue/15 text-white"
                                : "border-white/10 text-white/50 hover:border-white/25"
                            }`}
                            data-testid={`interest-${it.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            {it}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block font-mono text-xs tracking-wider text-white/45">MESSAGE</label>
                      <textarea
                        value={form.message}
                        onChange={set("message")}
                        rows={4}
                        placeholder="Tell us about your transformation initiative…"
                        className="w-full resize-none rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-nexis-blue"
                        data-testid="input-message"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="group flex w-full items-center justify-center gap-2 rounded-full bg-nexis-blue px-7 py-4 font-medium text-black shadow-[0_0_40px_rgba(0,200,255,0.3)] transition-all hover:shadow-[0_0_60px_rgba(0,200,255,0.5)] disabled:opacity-60"
                      data-testid="submit-lead"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {loading ? "Transmitting…" : "Send transmission"}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Calendly modal */}
      <AnimatePresence>
        {calOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
            onClick={() => setCalOpen(false)}
            data-testid="calendar-modal"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 200, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="relative h-[80vh] w-full max-w-3xl overflow-hidden rounded-3xl glass"
            >
              <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                <span className="font-display text-lg">Schedule a strategy session</span>
                <button onClick={() => setCalOpen(false)} className="rounded-full p-2 text-white/60 hover:bg-white/10 hover:text-white" data-testid="close-calendar">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <iframe
                title="Calendly"
                src={CALENDLY_URL}
                className="h-[calc(80vh-61px)] w-full bg-white"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="mb-2 block font-mono text-xs tracking-wider text-white/45">{label.toUpperCase()}</label>
      <input
        {...props}
        data-testid={testid}
        className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-nexis-blue"
      />
    </div>
  );
}
